'use client';
import { useEnvironmentContext } from '@susu/custom-made/providers/hooks/useEnvironmentContext';
import { getCdnImageBaseUrl, getCdnVideoBaseUrl } from '@susu/custom-made/utils/imageDefinition';
import { ON_BOARDING_ITEM_TEST_IDS } from '@susu/custom-made/utils/testIds';
import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';

import Button, { ButtonSize, ButtonType } from '../../button/button';
import { ONBOARDING_IMAGE_PATH, ONBOARDING_VIDEO_PATHS } from '../onBoardingAssets';
import './onBoardingItem.css';
import { useI18N } from '@susu/i18n/client';

const CONTAINER_CLASSES =
  'w-full h-full text-center flex flex-col justify-between items-center bg-gray-300 pt-40 md:pt-56 lg:pt-40 2xl:pt-56';

const LOGO_CONTAINER_CLASSES =
  'flex items-center justify-center text-gray-800 font-light text-xs md:text-sm lg:text-xs 2xl:text-sm leading-xs md:leading-sm lg:leading-xs 2xl:leading-sm [&>i]:w-20 [&>i]:h-20 [&>i]:bg-transparent [&>i]:bg-center [&>i]:bg-no-repeat [&>i]:flex [&>i]:mr-4';

const STEP_NO_CLASSES =
  'text-xs md:text-sm lg:text-xs 2xl:text-sm text-gray-800 font-light leading-xs md:leading-sm lg:leading-xs 2xl:leading-sm py-2 md:py-0 lg:py-2 2xl:py-0 relative';

const TITLE_BASE_CLASSES =
  'text-2xl md:text-[28px] lg:text-2xl 2xl:text-[28px] text-gray-800 font-medium leading-2xl md:leading-3xl lg:leading-2xl 2xl:leading-3xl tracking-[-0.36px] md:tracking-[-0.42px] lg:tracking-[-0.36px] 2xl:tracking-[-0.42px] max-w-[225px] md:max-w-[269px] lg:max-w-[218px] 2xl:max-w-[267px] min-h-64 md:min-h-72 lg:min-h-64 2xl:min-h-72 mt-4 md:mt-6 lg:mt-4 2xl:mt-[7px] mb-20 flex justify-center items-center absolute left-1/2 top-0 w-full will-change-[transform,opacity] [transition:transform_0.3s_cubic-bezier(0.5,0.05,0.1,1),opacity_0.3s_cubic-bezier(0.5,0.05,0.1,1)]';

const getTitleClasses = (index: number, currentStep: number) =>
  cn(
    TITLE_BASE_CLASSES,
    index === currentStep ? 'opacity-100' : 'opacity-0',
    index < currentStep && '[transform:translate3d(calc(-50%_-_40px),0,0)]',
    index > currentStep && '[transform:translate3d(calc(-50%_+_40px),0,0)]',
    index === currentStep && '[transform:translate3d(-50%,0,0)]',
  );

const DOT_AFTER_BASE =
  "after:content-[''] after:absolute after:left-0 after:h-4 after:rounded-[99px] after:bg-gray-600";

const getDotClasses = (
  index: number,
  currentStep: number,
  stepInit: boolean,
  animationDone: boolean,
  stepAmount: number,
) => {
  const noAnimation = currentStep === stepAmount - 1;
  const selected = currentStep === index && stepInit;
  const selectedBefore = currentStep > index || animationDone;
  const selectedDone = animationDone || (currentStep === stepAmount - 1 && currentStep === index);
  const isPosition0 = currentStep === 0;

  let afterWidthClass = 'after:w-0';
  if (selectedDone) {
    afterWidthClass = 'after:w-4';
  } else if (selected) {
    afterWidthClass = 'after:w-[15px]';
  }

  let afterTransitionClass = 'after:[transition:width_6s_linear]';
  if (isPosition0) {
    afterTransitionClass = 'after:[transition:width_2s_linear]';
  } else if (noAnimation || selectedBefore) {
    afterTransitionClass = 'after:[transition:none]';
  }

  return cn(
    'relative mx-[3.5px] h-4 bg-gray-400',
    DOT_AFTER_BASE,
    selected && !selectedDone ? 'w-[15px]' : 'w-4',
    selected ? 'rounded-[99px]' : 'rounded-full',
    afterWidthClass,
    selected ? 'after:visible' : 'after:invisible',
    afterTransitionClass,
  );
};

export const OnBoardingItem = ({
  titles,
  ctaText,
  stepAmount,
  currentStep,
  animationDone,
  ctaClick,
  changeStepClick,
}: {
  titles: string[];
  ctaText: string;
  stepAmount: number;
  currentStep: number;
  animationDone: boolean;
  ctaClick: () => void;
  changeStepClick: (step: number) => void;
}) => {
  const { t } = useI18N();
  const [stepInit, setStepInit] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { cdnDomain } = useEnvironmentContext();
  const cdnImageBaseUrl = getCdnImageBaseUrl(cdnDomain);
  const cdnVideoBaseUrl = getCdnVideoBaseUrl(cdnDomain);
  const videoUrls = ONBOARDING_VIDEO_PATHS.map(path => (path ? `${cdnVideoBaseUrl}${path}` : ''));
  const itemImageUrl = `${cdnImageBaseUrl}${ONBOARDING_IMAGE_PATH}`;

  useEffect(() => {
    setStepInit(true);
  }, []);

  useEffect(() => {
    videoRef.current?.load();
  }, [currentStep]);

  return (
    <div className={CONTAINER_CLASSES} data-testid={ON_BOARDING_ITEM_TEST_IDS.CONTAINER}>
      <div className="absolute inset-0 rounded-[8px]">
        {currentStep === 0 && (
          <img
            className="w-full h-full object-cover block rounded-[8px]"
            src={itemImageUrl}
            alt=""
            fetchPriority="high"
          />
        )}
        {currentStep > 0 && (
          <video
            ref={videoRef}
            className="w-full h-full object-cover block rounded-[8px]"
            autoPlay
            muted
            playsInline
            preload="metadata"
          >
            <source src={videoUrls[currentStep]} type="video/webm" />
          </video>
        )}
      </div>
      <div
        className={cn(
          'absolute top-0 left-0 bottom-80 right-[60%] z-1 md:hidden',
          currentStep === 0 ? 'cursor-auto' : 'cursor-pointer',
        )}
        onClick={() => {
          if (currentStep !== 0) {
            changeStepClick(currentStep - 1);
          }
        }}
      ></div>
      <div
        className={cn(
          'absolute top-0 bottom-80 right-0 left-[40%] z-1 md:hidden',
          stepAmount <= currentStep + 1 ? 'cursor-auto' : 'cursor-pointer',
        )}
        onClick={() => {
          if (stepAmount > currentStep + 1) {
            changeStepClick(currentStep + 1);
          }
        }}
      ></div>
      <div
        className={cn('cm-ob-slide-arrow cm-ob-slide-prev', {
          'cm-ob-disabled': currentStep === 0,
        })}
        onClick={() => {
          if (currentStep !== 0) {
            changeStepClick(currentStep - 1);
          }
        }}
      >
        {t('onBoarding.previous')}
      </div>
      <div
        className={cn('cm-ob-slide-arrow cm-ob-slide-next', {
          'cm-ob-disabled': stepAmount === currentStep + 1,
        })}
        onClick={() => {
          if (stepAmount > currentStep + 1) {
            changeStepClick(currentStep + 1);
          }
        }}
      >
        {t('onBoarding.next')}
      </div>
      <div className="w-full relative z-1">
        {currentStep === 0 && (
          <div className={LOGO_CONTAINER_CLASSES}>
            <i style={{ backgroundImage: `url(${cdnImageBaseUrl}/svg/icon-custom-made.svg)` }}></i>{' '}
            {t('onBoarding.logoAlt')}
          </div>
        )}
        {currentStep > 0 && <div className={STEP_NO_CLASSES}>0{currentStep}</div>}
        <div className="w-full relative">
          {titles.slice(0, stepAmount).map((title, index) => (
            <div key={`title_${title}_${index}`} className={getTitleClasses(index, currentStep)}>
              {title}
            </div>
          ))}
        </div>
        <div className="mt-[88px] md:mt-[98px] lg:mt-[88px] 2xl:mt-[99px] flex justify-center">
          {[...Array(stepAmount)].map((stepNo, index) => (
            <span
              key={`dots_${index}`}
              className={getDotClasses(index, currentStep, stepInit, animationDone, stepAmount)}
            ></span>
          ))}
        </div>
      </div>
      <div className="cm-ob-button-container flex mb-32 z-1 md:mb-0 md:absolute md:bottom-48 lg:bottom-40 2xl:bottom-48">
        <Button
          onClick={ctaClick}
          type={ButtonType.Primary}
          isFullSize={true}
          size={ButtonSize.Default}
          testId={ON_BOARDING_ITEM_TEST_IDS.START_DESIGNING}
        >
          <img
            className="w-20 h-20 mr-6"
            src={`${cdnImageBaseUrl}/svg/icon-custom-made.svg`}
            alt={t('onBoarding.logoAlt')}
          />
          {ctaText}
        </Button>
      </div>
    </div>
  );
};
